import type { Cliente } from "@base/contracts";
import {
  Button,
  CabecalhoDePagina,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  EstadoCarregando,
  EstadoDeErro,
  EstadoVazio,
  Etiqueta,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Tabela,
  type ColunaDaTabela,
} from "@base/ui";
import { Building2, MoreHorizontal, Plus } from "lucide-react";
import { useState } from "react";
import { mensagemDoErro } from "../../../shared/http/mensagem-de-erro";
import { useClienteSelecionado } from "../application/clientes.contexto";
import { useAtualizarCliente, useCriarCliente, useExcluirCliente } from "../application/use-clientes";
import { FormularioDeCliente } from "./formulario-de-cliente";

type Edicao = { readonly modo: "criar" } | { readonly modo: "renomear"; readonly cliente: Cliente };

/** Clientes da Monte (so para a equipe Monte): cadastrar, renomear, desativar e excluir. */
export function PaginaDeClientes() {
  const { clientes, carregando, erro, selecionado, selecionar } = useClienteSelecionado();
  const criar = useCriarCliente();
  const atualizar = useAtualizarCliente();
  const [edicao, setEdicao] = useState<Edicao | null>(null);
  const [excluindo, setExcluindo] = useState<Cliente | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  function fecharEdicao() {
    setEdicao(null);
    criar.reset();
    atualizar.reset();
  }

  const colunas: readonly ColunaDaTabela<Cliente>[] = [
    {
      chave: "nome",
      titulo: "Cliente",
      celula: (cliente) => (
        <span className="font-medium">
          {cliente.nome}
          {cliente.id === selecionado?.id ? (
            <span className="ml-2 text-xs font-normal text-muted-foreground">(selecionado)</span>
          ) : null}
        </span>
      ),
    },
    {
      chave: "situacao",
      titulo: "Situação",
      celula: (cliente) =>
        cliente.ativo ? <Etiqueta tom="sucesso">Ativo</Etiqueta> : <Etiqueta tom="neutro">Desativado</Etiqueta>,
    },
    {
      chave: "acoes",
      titulo: "Ações",
      celula: (cliente) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" aria-label={`Ações para ${cliente.nome}`}>
              <MoreHorizontal aria-hidden="true" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onSelect={() => selecionar(cliente.id)}>Selecionar</DropdownMenuItem>
            <DropdownMenuItem onSelect={() => setEdicao({ modo: "renomear", cliente })}>
              Renomear
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              variant={cliente.ativo ? "destructive" : "default"}
              onSelect={() =>
                atualizar.mutate(
                  { id: cliente.id, dados: { ativo: !cliente.ativo } },
                  {
                    onSuccess: () =>
                      setAviso(
                        cliente.ativo
                          ? `${cliente.nome} foi desativado. As pessoas da clínica perderam o acesso.`
                          : `${cliente.nome} foi reativado.`,
                      ),
                    onError: (falha) => setAviso(mensagemDoErro(falha)),
                  },
                )
              }
            >
              {cliente.ativo ? "Desativar" : "Reativar"}
            </DropdownMenuItem>
            <DropdownMenuItem variant="destructive" onSelect={() => setExcluindo(cliente)}>
              Excluir
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-secao">
      <CabecalhoDePagina
        rotulo="Gestão"
        titulo="Clientes"
        descricao="As clínicas atendidas pela Monte. Cada uma vê apenas os próprios dados."
        acoes={
          <Button onClick={() => setEdicao({ modo: "criar" })}>
            <Plus aria-hidden="true" />
            Novo cliente
          </Button>
        }
      />

      <p
        role="status"
        aria-live="polite"
        className={aviso ? "rounded-xl bg-accent px-4 py-3 text-sm text-accent-foreground" : "sr-only"}
      >
        {aviso}
      </p>

      {carregando ? (
        <EstadoCarregando rotulo="Carregando clientes" />
      ) : erro ? (
        <EstadoDeErro mensagem={mensagemDoErro(erro)} />
      ) : clientes.length === 0 ? (
        <EstadoVazio
          titulo="Nenhum cliente cadastrado"
          descricao="Cadastre a primeira clínica para começar."
          icone={<Building2 className="size-6" aria-hidden="true" />}
        />
      ) : (
        <Tabela legenda="Clientes da Monte" colunas={colunas} itens={clientes} chaveDe={(cliente) => cliente.id} />
      )}

      <ConfirmacaoDeExclusao
        cliente={excluindo}
        aoFechar={() => setExcluindo(null)}
        aoExcluir={(cliente) => {
          setExcluindo(null);
          setAviso(`${cliente.nome} foi excluído.`);
        }}
      />

      <Dialog open={edicao !== null} onOpenChange={(aberto) => (aberto ? undefined : fecharEdicao())}>
        <DialogContent tamanho="formulario">
          <DialogHeader rotulo="Gestão">
            <DialogTitle>{edicao?.modo === "renomear" ? "Renomear cliente" : "Novo cliente"}</DialogTitle>
            <DialogDescription>
              {edicao?.modo === "renomear"
                ? "O nome novo aparece para todos que acessam este cliente."
                : "Depois de cadastrar, dê acesso às pessoas da clínica em Gestão › Acessos."}
            </DialogDescription>
          </DialogHeader>
          {edicao?.modo === "criar" ? (
            <FormularioDeCliente
              rotuloDoBotao="Cadastrar cliente"
              enviando={criar.isPending}
              erro={criar.error}
              aoEnviar={(nome) =>
                criar.mutate(nome, {
                  onSuccess: (cliente) => {
                    setAviso(`${cliente.nome} foi cadastrado.`);
                    fecharEdicao();
                  },
                })
              }
            />
          ) : edicao?.modo === "renomear" ? (
            <FormularioDeCliente
              nomeInicial={edicao.cliente.nome}
              rotuloDoBotao="Salvar nome"
              enviando={atualizar.isPending}
              erro={atualizar.error}
              aoEnviar={(nome) =>
                atualizar.mutate(
                  { id: edicao.cliente.id, dados: { nome } },
                  {
                    onSuccess: () => {
                      setAviso("Nome atualizado.");
                      fecharEdicao();
                    },
                  },
                )
              }
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ConfirmacaoDeExclusao({
  cliente,
  aoFechar,
  aoExcluir,
}: {
  readonly cliente: Cliente | null;
  readonly aoFechar: () => void;
  readonly aoExcluir: (cliente: Cliente) => void;
}) {
  const excluir = useExcluirCliente();

  return (
    <Dialog
      open={cliente !== null}
      onOpenChange={(aberto) => {
        if (!aberto) {
          excluir.reset();
          aoFechar();
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Excluir {cliente?.nome}?</DialogTitle>
          <DialogDescription>
            O cliente some da lista e ninguém acessa mais o painel dele. O histórico fica no banco.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          {excluir.isError ? (
            <p role="alert" className="text-sm text-destructive">
              {mensagemDoErro(excluir.error)}
            </p>
          ) : null}
          <Button
            variant="destructive"
            disabled={excluir.isPending || cliente === null}
            onClick={() => {
              if (cliente) excluir.mutate(cliente.id, { onSuccess: () => aoExcluir(cliente) });
            }}
          >
            {excluir.isPending ? "Excluindo…" : "Excluir cliente"}
          </Button>
          <Button variant="outline" onClick={aoFechar}>
            Cancelar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
